// src/components/gestionEvento/forms/InfoGeneralForm.js

import React, { useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { PhotoCamera } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import ImageManagerModal from './ImageManagerModal';

// ────────────────────────────────────────────────────────────
// Formateador numérico (precio) — sin cambios de interfaz
// ────────────────────────────────────────────────────────────
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, name, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      thousandSeparator=","
      decimalSeparator="."
      isNumericString
      prefix="$"
      decimalScale={2}
      fixedDecimalScale
      allowNegative={false}
      onValueChange={(values) => {
        onChange(name, values.value || '0');
      }}
    />
  );
});

// ────────────────────────────────────────────────────────────
// Estilos consistentes tipo “pill/soft” para botones
// ────────────────────────────────────────────────────────────
const pillBase = (t) => ({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 700,
  px: 2, // respiración lateral
  py: 1,
});

const primaryPill = (t) => ({
  ...pillBase(t),
  boxShadow: `0 6px 18px ${alpha(t.palette.primary.main, 0.18)}`,
});

const softPrimaryPill = (t) => ({
  ...pillBase(t),
  bgcolor: alpha(t.palette.primary.main, 0.06),
  border: `1px solid ${alpha(t.palette.primary.main, 0.28)}`,
  '&:hover': {
    bgcolor: alpha(t.palette.primary.main, 0.1),
    borderColor: alpha(t.palette.primary.main, 0.4),
  },
});

const dangerPill = (t) => ({
  ...pillBase(t),
  border: `1px solid ${alpha(t.palette.error.main, 0.35)}`,
  bgcolor: alpha(t.palette.error.main, 0.04),
  color: t.palette.error.main,
  '&:hover': {
    bgcolor: alpha(t.palette.error.main, 0.08),
    borderColor: alpha(t.palette.error.main, 0.5),
  },
});

const InfoGeneralForm = ({
  formData = {},
  errors = {},
  onChange,
  imagenPreview,
  handlePortadaChange,
  logoPreview,
  handleLogoChange,
  isUploadingPortada,
  isUploadingLogo,
  uploadProgressPortada,
  uploadProgressLogo,
  portadaInputRef,
  logoInputRef,
  competenciaId,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('logo');
  const [modalScope, setModalScope] = useState('competition'); // 'competition' | 'user'

  const handleOpenModal = (type, scope = 'competition') => {
    setModalType(type);
    setModalScope(scope);
    setModalOpen(true);
  };

  const handleSelectImage = (imageUrl) => {
    onChange(modalType === 'logo' ? 'logo' : 'imagenPortada', imageUrl);
  };

  const sectionSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
  };

  return (
    <>
      <Grid container spacing={2.5} disableEqualOverflow sx={sectionSx}>
        {/* Información Básica */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
            Información Básica
          </Typography>
        </Grid>

        {/* Nombre de la competencia */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="Nombre de la Competencia"
            name="nombre"
            fullWidth
            value={formData.nombre || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            error={Boolean(errors.nombre)}
            helperText={errors.nombre}
            variant="outlined"
          />
        </Grid>

        {/* Subtítulo */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Subtítulo o Eslogan"
            name="subtitulo"
            fullWidth
            value={formData.subtitulo || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            variant="outlined"
          />
        </Grid>

        {/* Fechas */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mt: 2 }}>
            Fechas Importantes
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Inicio Competencia"
            name="fechaInicioCompetencia"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.fechaInicioCompetencia || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Fin Competencia"
            name="fechaFinCompetencia"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.fechaFinCompetencia || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Inicio Inscripciones"
            name="fechaInicioInscripciones"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.fechaInicioInscripciones || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Fin Inscripciones"
            name="fechaFinInscripciones"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.fechaFinInscripciones || ''}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            variant="outlined"
          />
        </Grid>

        {/* Configuración */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mt: 2 }}>
            Configuración
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required variant="outlined">
            <InputLabel id="tipo-competencia-label">Tipo de Competencia</InputLabel>
            <Select
              labelId="tipo-competencia-label"
              label="Tipo de Competencia"
              name="tipoCompetencia"
              value={formData.tipoCompetencia || ''}
              onChange={(e) => onChange(e.target.name, e.target.value)}
              error={Boolean(errors.tipoCompetencia)}
            >
              <MenuItem value="ruta">🏃 Ruta</MenuItem>
              <MenuItem value="pista_y_campo">🏃‍♀️ Pista y Campo</MenuItem>
              <MenuItem value="campo_traviesa">🌲 Campo Traviesa</MenuItem>
              <MenuItem value="otros">🏆 Otros</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required variant="outlined">
            <InputLabel id="estado-label">Estado</InputLabel>
            <Select
              labelId="estado-label"
              label="Estado"
              name="estado"
              value={formData.estado || ''}
              onChange={(e) => onChange(e.target.name, e.target.value)}
              error={Boolean(errors.estado)}
            >
              <MenuItem value="pendiente">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Borrador" color="warning" size="small" />
                  Pendiente
                </Box>
              </MenuItem>
              <MenuItem value="publicado">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Activo" color="success" size="small" />
                  Publicado
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Precio de Inscripciones"
            name="precioInscripciones"
            placeholder="$0.00"
            fullWidth
            value={formData.precioInscripciones || '0'}
            onChange={(name, value) => onChange(name, value)}
            InputProps={{
              inputComponent: NumericFormatCustom,
              inputProps: { name: 'precioInscripciones' },
            }}
            error={Boolean(errors.precioInscripciones)}
            helperText={errors.precioInscripciones}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />
        </Grid>

        {/* Espacio */}
        <Grid item xs={12} sm={6} />

        {/* Imágenes */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mt: 2 }}>
            Imágenes
          </Typography>
        </Grid>

        {/* Logo */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderColor: 'primary.main'
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                Logo de la Competencia
              </Typography>

              {/* Preview */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={(t) => ({
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    border: '2px dashed',
                    borderColor: formData.logo ? t.palette.success.main : 'grey.300',
                    borderRadius: '12px',
                    backgroundColor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  })}
                >
                  {formData.logo ? (
                    <Box component="img" src={formData.logo} alt="Logo Preview" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">Sin logo</Typography>
                  )}
                </Box>
              </Box>

              {formData.logo && !isUploadingLogo && (
                <Alert
                  severity="success"
                  icon={<CheckCircle />}
                  sx={{ mb: 2, py: 0.5 }}
                >
                  <Typography variant="caption">
                    Logo cargado correctamente
                  </Typography>
                </Alert>
              )}

              {isUploadingLogo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="primary" gutterBottom>
                    Subiendo logo...
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgressLogo || 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: 'linear-gradient(90deg, #FF6B6B, #4ECDC4)'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" align="center" display="block">
                    {Math.round(uploadProgressLogo || 0)}%
                  </Typography>
                </Box>
              )}

              {/* Botones consistentes */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={formData.logo ? 'outlined' : 'contained'}
                  startIcon={isUploadingLogo ? <CloudUpload /> : <PhotoCamera />}
                  onClick={() => handleOpenModal('logo')}
                  disabled={isUploadingLogo}
                  sx={(t) => (formData.logo ? softPrimaryPill(t) : primaryPill(t))}
                  fullWidth
                >
                  {isUploadingLogo ? 'Subiendo...' : (formData.logo ? 'Cambiar logo' : 'Subir logo')}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => handleOpenModal('logo', 'user')}
                  disabled={isUploadingLogo}
                  sx={(t) => softPrimaryPill(t)}
                  fullWidth
                >
                  Usar logo de organización
                </Button>

                {formData.logo && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={() => onChange('logo', '')}
                    disabled={isUploadingLogo}
                    sx={(t) => dangerPill(t)}
                  >
                    Quitar
                  </Button>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Recomendado: 200x200px, formato cuadrado, PNG o JPG
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Portada */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderColor: 'primary.main'
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                Imagen de Portada
              </Typography>

              {/* Preview */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={(t) => ({
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    border: '2px dashed',
                    borderColor: formData.imagenPortada ? t.palette.success.main : 'grey.300',
                    borderRadius: '12px',
                    backgroundColor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  })}
                >
                  {formData.imagenPortada ? (
                    <Box component="img" src={formData.imagenPortada} alt="Portada Preview" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">Sin portada</Typography>
                  )}
                </Box>
              </Box>

              {formData.imagenPortada && !isUploadingPortada && (
                <Alert
                  severity="success"
                  icon={<CheckCircle />}
                  sx={{ mb: 2, py: 0.5 }}
                >
                  <Typography variant="caption">
                    Imagen de portada cargada correctamente
                  </Typography>
                </Alert>
              )}

              {isUploadingPortada && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="primary" gutterBottom>
                    Subiendo imagen de portada...
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgressPortada || 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" align="center" display="block">
                    {Math.round(uploadProgressPortada || 0)}%
                  </Typography>
                </Box>
              )}

              {/* Botones consistentes */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={formData.imagenPortada ? 'outlined' : 'contained'}
                  startIcon={isUploadingPortada ? <CloudUpload /> : <PhotoCamera />}
                  onClick={() => handleOpenModal('portada')}
                  disabled={isUploadingPortada}
                  sx={(t) => (formData.imagenPortada ? softPrimaryPill(t) : primaryPill(t))}
                  fullWidth
                >
                  {isUploadingPortada ? 'Subiendo...' : (formData.imagenPortada ? 'Cambiar portada' : 'Subir portada')}
                </Button>

                {formData.imagenPortada && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={() => onChange('imagenPortada', '')}
                    disabled={isUploadingPortada}
                    sx={(t) => dangerPill(t)}
                  >
                    Quitar
                  </Button>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Recomendado: 800x450px, formato 16:9, JPG o PNG
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ImageManagerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelectImage={handleSelectImage}
        currentImageUrl={modalType === 'logo' ? formData.logo : formData.imagenPortada}
        type={modalType}
        competenciaId={competenciaId}
        scope={modalScope}
      />
    </>
  );
};

InfoGeneralForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  imagenPreview: PropTypes.string,
  handlePortadaChange: PropTypes.func.isRequired,
  logoPreview: PropTypes.string,
  handleLogoChange: PropTypes.func.isRequired,
  isUploadingPortada: PropTypes.bool,
  isUploadingLogo: PropTypes.bool,
  uploadProgressPortada: PropTypes.number,
  uploadProgressLogo: PropTypes.number,
  portadaInputRef: PropTypes.object,
  logoInputRef: PropTypes.object,
  competenciaId: PropTypes.string,
};

export default InfoGeneralForm;
